import { format, subHours } from 'date-fns';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../../../hooks/useGetGeneralAppData';
import ConsultsService from '../../../../../../../services/ConsultsService';
import ExportXlsx from '../../../../../../../utils/ExportXlsx';
import { IReportApiResponse, Value, ValuePiece } from './types';

interface IuseDownloadOtimizationModal {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setDownloadModalShow: Dispatch<SetStateAction<boolean>>;
}
/* eslint-disable @typescript-eslint/no-empty-function */
export default function useDownloadOtimizationModal({ setIsLoading, setDownloadModalShow }: IuseDownloadOtimizationModal) {
  const [specificPeriod, setSpecificPeriod] = useState<Value>([new Date(), new Date()]);
  const [specificPeriodLabel, setSpecificPeriodLabel] = useState(`De ${format(new Date(), 'dd/MM/yyyy')} até ${format(new Date(), 'dd/MM/yyyy')}`);

  const { apiCall } = useApiCall();
  const { userCode, companyCode, userProfile } = useGetGeneralAppData();

  function handleSpecificPeriodChange(value: Value) {
    if (Array.isArray(value)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const adjustedByTimezone = value.map((date) => subHours(date!, 3));
      setSpecificPeriod([adjustedByTimezone[0], adjustedByTimezone[1]]);

      const periodLabel = `De ${format(value[0] as Date, 'dd/MM/yyyy')} até ${format(value[1] as Date, 'dd/MM/yyyy')}`;
      setSpecificPeriodLabel(periodLabel);
    }
  }

  function getFirstAndLastPeriod(value: Value): { first: ValuePiece, last: ValuePiece } {
    if (value instanceof Array) {
      // Se for um Range, acessa o primeiro e último elemento
      const [first, last] = value;
      return { first, last };
    } else {
      // Se for um único valor, tanto o primeiro quanto o último são o próprio valor
      return { first: value, last: value };
    }
  }

  const downloadReport = useCallback(async () => {
    const period = getFirstAndLastPeriod(specificPeriod);

    await apiCall({
      apiToCall: ConsultsService.getOtimizationBaseReport,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        data1: period.first!.toISOString()!.split('T')[0],
        data2: period.last!.toISOString().split('T')[0],
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (apiResponse: IReportApiResponse) => {
        if (!apiResponse.codigo) {
          toast.error(`Não foi possível baixar a planilha de otimização (${apiResponse.msg})`);
          return;
        }

        if (!apiResponse.temRegistros) {
          toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
          return;
        }

        const report = apiResponse.rel5 as unknown as { [key: string]: string | number }[];
        const mappedReportList = report?.map((rp) => ({
          CodFunc: rp.codFunc ?? '',
          CodConsulta: rp.codConsulta ?? '',
          CNPJ: rp.cnpj ?? '',
          Empresa: rp.empresa ?? '',
          CodLocal: rp.codLTArq ?? '',
          DescLocal: rp.localTrabArq ?? '',
          CodLocalAtual: rp.codLTAtual ?? '',
          DescLocalAtual: rp.localTrabAtual ?? '',
          CPF: rp.cpf ?? '',
          Matricula: rp.matricula ?? '',
          Nome: rp.nome ?? '',
          Logradouro: rp.funcLogradouro ?? '',
          Numero: rp.funcEndNum ?? '',
          Complem: rp.funcComplemento ?? '',
          CEPAntes: rp.funcCEPArq ?? '',
          CEPAtual: rp.funcCEPAtual ?? '',
          DifCEP: rp.diferencaCEP ?? '',
          Bairro: rp.funcBairro ?? '',
          Cidade: rp.funcCidade ?? '',
          UF: rp.funcUF ?? '',
          'E-mail': rp.mail ?? '',
          HoraEntra: rp.horaEntra ?? '',
          HoraSai: rp.horaSai ?? '',
          TurnoDias: rp.TurnoDias ?? '',
          'Valor Dia Atual': rp.valorVTInicial ?? '',
          'Valor Mês Atual': rp.valorVTInicialMes ?? '',
          'Valor dia Rot': rp.valorVTRot ?? '',
          'Valor Mês Rot': rp.valorVTRotMes ?? '',
          'Otimiz Dia': rp.otimizacaoDia ?? '',
          'Otimiz Mês': rp.otimizacaoMes ?? '',
          'Tem GS': rp.gestaoSaldo,
          'StatusResultado': rp.statusResultado ?? '',
          'StatusConsulta': rp.statusConsulta ?? '',
          'CanceladoVT': rp.canceladoVT ? 'SIM' : 'NÃO',
          'MudouCadastro': rp.mudouCadastro ? 'SIM' : 'NÃO',
          'DataCadastro': String(rp.dataCadastro).includes('1900') ? '' : format(new Date(rp.dataCadastro), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
          DataCadAtualizado: String(rp.dataCadAtualizado).includes('1900') ? '' : format(new Date(rp.dataCadAtualizado), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
          'Data Inicial IA': String(rp.Data_Inicial_IA).includes('1900') ? '' : rp.Data_Inicial_IA,
          'Ultimo Envio IA': String(rp.Ultimo_Envio_IA).includes('1900') ? '' : rp.Ultimo_Envio_IA,
          Canal: rp.canal ?? '',
          InicioApp: String(rp.inicioApp).includes('1900') ? '' : format(new Date(rp.inicioApp), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
          SituacaoCadastro: rp.situacaoCadastro ?? '',
          DataImplantacao: String(rp.dataImplantacao).includes('1900') ? '' : format(new Date(rp.dataImplantacao), 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
          CompraVT: rp.compraVT ?? '',
          CodVT1: rp.r1VT1_CodVT ?? '',
          DescVT1: rp.r1VT1_Descr ?? '',
          QtdaVT1: rp.r1VT1_Qtd ?? '',
          VlrVT1: rp.r1VT1_Tarifa ?? '',
          CodVT2: rp.r1VT2_CodVT ?? '',
          DescVT2: rp.r1VT2_Descr ?? '',
          QtdaVT2: rp.r1VT2_Qtd ?? '',
          VlrVT2: rp.r1VT2_Tarifa ?? '',
          CodVT3: rp.r1VT3_CodVT ?? '',
          DescVT3: rp.r1VT3_Descr ?? '',
          QtdaVT3: rp.r1VT3_Qtd ?? '',
          VlrVT3: rp.r1VT3_Tarifa ?? '',
          CodVT4: rp.r1VT4_CodVT ?? '',
          DescVT4: rp.r1VT4_Descr ?? '',
          QtdaVT4: rp.r1VT4_Qtd ?? '',
          VlrVT4: rp.r1VT4_Tarifa ?? '',
          CodVT5: rp.r1VT5_CodVT ?? '',
          DescVT5: rp.r1VT5_Descr ?? '',
          QtdaVT5: rp.r1VT5_Qtd ?? '',
          VlrVT5: rp.r1VT5_Tarifa ?? '',
          'Dist a Pé': rp.distaciaPe ?? '',
          'DistOnibus_Ida': rp.distOnibus_Ida ?? '',
          'DistOnibus_Volta': rp.distOnibus_Volta ?? '',
          'DistTrem_Ida': rp.distTrem_Ida ?? '',
          'DistTrem_Volta': rp.distTrem_Volta ?? '',
        }));

        ExportXlsx({
          filename: `Relatório Otimização Base - ${specificPeriodLabel}`,
          data: mappedReportList,
        });

        setDownloadModalShow(false);
      },
      catchMessage: 'Não foi possível baixar a planilha de otimização.',
    });
  }, [apiCall, companyCode, setDownloadModalShow, setIsLoading, specificPeriod, specificPeriodLabel, userCode, userProfile]);

  return {
    downloadReport,
    handleSpecificPeriodChange,
    specificPeriod,
    specificPeriodLabel,
  };
}
